import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  Validator,
  AbstractControl,
  ValidationErrors,
  NG_VALIDATORS,
} from '@angular/forms';
import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscriber } from 'rxjs';
import { ComponentModel, ICondition, IValue } from '@platform500services/p500-ui-kit';

@Component({
  selector: 'gtd-payments-system-condition',
  templateUrl: './condition.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConditionComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ConditionComponent),
      multi: true,
    },
  ],
})
export class ConditionComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  @Input() public conditions: ICondition[] = [];
  @Input() public index = 0;
  @Input() public name = '';
  @Input() public container?: string;
  @Input() getValueComponentByType: (formControl: FormControl, key: string, type: string, label: string) => ComponentModel;
  @Input() getSignsData: (type: string) => { [key: string]: string };

  @Output() remove = new EventEmitter<number>();

  public form: FormGroup;
  public conditionsData = {};
  public signsData = {};
  public valueComponent: ComponentModel;

  private initControlIndex = new Date().getUTCMilliseconds();
  private subscribers: any = {};

  constructor(private fb: FormBuilder, private cd: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.conditions.forEach((condition) => {
      this.conditionsData[condition.key] = condition.label;
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      [this.getNamedKey('key')]: this.fb.control(null, Validators.required),
      [this.getNamedKey('operator')]: this.fb.control(null, Validators.required),
      [this.getNamedKey('value')]: this.fb.control(null, Validators.required),
    });

    this.subscribers.keyControlValueSubscriber = this.getControlByKey('key')
      .valueChanges.pipe(debounceTime(0), distinctUntilChanged())
      .subscribe((keyFormControlValue) => {
        this.prepareDataByKey(keyFormControlValue);
        this.form.get(this.getNamedKey('operator')).reset();
        this.form.get(this.getNamedKey('value')).reset();
      });

    this.subscribers.operatorControlValueSubscriber = this.getControlByKey('operator')
      .valueChanges.pipe(debounceTime(0), distinctUntilChanged())
      .subscribe((operatorFormControlValue) => {
        this.emitChange({
          ...this.form.value,
          [this.getNamedKey('operator')]: operatorFormControlValue,
        });
      });

    this.subscribers.valueControlValueSubscriber = this.getControlByKey('value')
      .valueChanges.pipe(debounceTime(0))
      .subscribe((valueFormControlValue) => {
        this.emitChange({
          ...this.form.value,
          [this.getNamedKey('value')]: valueFormControlValue,
        });
      });
  }

  ngOnDestroy() {
    for (const subscriberKey in this.subscribers) {
      if (Object.prototype.hasOwnProperty.call(this.subscribers, subscriberKey)) {
        if (this.subscribers[subscriberKey] instanceof Subscriber) {
          this.subscribers[subscriberKey].unsubscribe();
        }
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(value: IValue = {}): void {
    const newValue = {};
    Object.entries(value).forEach(([k, v]) => {
      if (k === 'key' || k === 'operator' || k === 'value') {
        newValue[this.getNamedKey(k)] = v;
      }
    });
    this.prepareDataByKey(newValue[this.getNamedKey('key')]);
    this.form.patchValue(newValue, { emitEvent: false });
  }

  emitChange(value: { [key: string]: any }): void {
    this.onChange({
      key: value[this.getNamedKey('key')],
      operator: value[this.getNamedKey('operator')],
      value: value[this.getNamedKey('value')],
    });
    this.onTouch();
  }

  prepareDataByKey(key: string): void {
    const type = this.getConditionField(key, 'type');
    const label = this.getConditionField(key, 'label');
    this.valueComponent = this.getValueComponentByType(
      this.getControlByKey('value'),
      this.getNamedKey('value'),
      type ? type : '',
      label ? label : 'Value'
    );
    this.cd.markForCheck();
    this.signsData = this.getSignsData(type);
  }

  getNamedKey(key: string): string {
    return `${this.name ? this.name : 'condition'}-${this.initControlIndex}-${key}`;
  }

  getControlByKey(key: string): FormControl {
    return this.form.get(this.getNamedKey(key)) as FormControl;
  }

  getConditionField(keyValue: string, field: string): string | null {
    const condition = this.conditions.find((cond) => cond.key === keyValue);
    return condition ? condition[field] : '';
  }

  deleteCondition(): void {
    this.remove.emit(this.index);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    for (const key in control.value) {
      if (
        control.value[key] === null ||
        control.value[key] === '' ||
        control.value[key] === undefined ||
        (Array.isArray(control.value[key]) && (control.value[key] as any[]).length === 0)
      ) {
        return { invalid: true };
      }
    }
    return null;
  }

  protected onChange: (value: any) => void;
  protected onTouch: () => void;
}
